import React, { useEffect, useRef } from 'react';
import { AirplaneState } from '../types';
import { useMap } from '../MapContext';
import mapboxgl from 'mapbox-gl';

interface AirplaneProps {
  airplane: AirplaneState;
}

const getAltitudeColor = (altitude: number): string => {
  if (altitude < 5000) return '#3CAEA3';
  if (altitude < 15000) return '#F6D55C';
  if (altitude < 25000) return '#ED553B';
  return '#9370DB';
};

const formatAltitude = (altitude: number | string): string => {
  if (altitude === 'ground' || altitude === 0) return 'ground';
  return `${Math.round(Number(altitude))} ft`;
};

const Airplane: React.FC<AirplaneProps> = ({ airplane }) => {
  const mapboxMap = useMap();
  const markerRef = useRef<mapboxgl.Marker | null>(null);

  useEffect(() => {
    if (!mapboxMap) return;

    const altValue = typeof airplane.altitude === 'number'
      ? airplane.altitude
      : 0;

    const isMarkerInBounds = (position: [number, number]): boolean => {
      const bounds = mapboxMap.getBounds();
      const [lng, lat] = position;
      const padding = 0.05;
      const sw = bounds.getSouthWest();
      const ne = bounds.getNorthEast();

      return lng >= sw.lng - (ne.lng - sw.lng) * padding &&
             lng <= ne.lng + (ne.lng - sw.lng) * padding &&
             lat >= sw.lat - (ne.lat - sw.lat) * padding &&
             lat <= ne.lat + (ne.lat - sw.lat) * padding;
    };

    const handleMoveEnd = () => {
      if (!markerRef.current) return;
      const markerEl = markerRef.current.getElement();
      if (isMarkerInBounds(airplane.position)) {
        markerEl.classList.remove('out-of-bounds');
      } else {
        markerEl.classList.add('out-of-bounds');
      }
    };

    if (!markerRef.current) {
      const el = document.createElement('div');
      el.className = 'airplane-marker';

      const altitudeColor = getAltitudeColor(altValue);
      const altitudeDisplay = formatAltitude(airplane.altitude);

      el.innerHTML = `
        <div class="airplane-icon" style="transform: rotate(${airplane.rotation}deg);">
          <div class="airplane-svg-colored"
               style="background-color: ${altitudeColor};
                      -webkit-mask-image: url('/AirplaneSVG.svg');
                      mask-image: url('/AirplaneSVG.svg');">
          </div>
        </div>
        <div class="airplane-label" style="border-left: 3px solid ${altitudeColor};">${airplane.airplaneId} - ${altitudeDisplay}</div>
      `;

      markerRef.current = new mapboxgl.Marker({
        element: el,
        anchor: 'center',
        rotationAlignment: 'map',
        pitchAlignment: 'map'
      })
      .setLngLat([airplane.position[0], airplane.position[1]])
      .addTo(mapboxMap);

      if (!isMarkerInBounds(airplane.position)) {
        markerRef.current.getElement().classList.add('out-of-bounds');
      }

      mapboxMap.on('moveend', handleMoveEnd);
    } else {
      markerRef.current.setLngLat([airplane.position[0], airplane.position[1]]);

      const markerEl = markerRef.current.getElement();
      if (isMarkerInBounds(airplane.position)) {
        markerEl.classList.remove('out-of-bounds');
      } else {
        markerEl.classList.add('out-of-bounds');
      }

      const altitudeColor = getAltitudeColor(altValue);
      const altitudeDisplay = formatAltitude(airplane.altitude);

      const iconEl = markerEl.querySelector('.airplane-icon');
      if (iconEl) {
        iconEl.setAttribute('style', `transform: rotate(${airplane.rotation}deg);`);
        const svgEl = iconEl.querySelector('.airplane-svg-colored');
        if (svgEl) {
          svgEl.setAttribute('style',
            `background-color: ${altitudeColor};
             -webkit-mask-image: url('/AirplaneSVG.svg');
             mask-image: url('/AirplaneSVG.svg');`
          );
        }
      }

      const labelEl = markerEl.querySelector('.airplane-label');
      if (labelEl) {
        labelEl.textContent = `${airplane.airplaneId} - ${altitudeDisplay}`;
        labelEl.setAttribute('style', `border-left: 3px solid ${altitudeColor};`);
      }
    }

    return () => {
      if (markerRef.current) {
        markerRef.current.remove();
        markerRef.current = null;
      }
      mapboxMap.off('moveend', handleMoveEnd);
    };
  }, [airplane, mapboxMap]);

  return null;
};

export default Airplane;
